import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Shield } from 'lucide-react';
import { apiFetch } from '../utils/api';
import SharedDownloadView from '../components/shared-access/SharedDownloadView';
import SharedRequestDetails from '../components/shared-access/SharedRequestDetails';
import SharedUploadView from '../components/shared-access/SharedUploadView';
import {
  SharedAccessErrorState,
  SharedAccessPinRequiredState,
  SharedAccessValidatingState
} from '../components/shared-access/SharedAccessStates';
import { formatRecordTypeLabel } from '../components/shared-access/sharedAccessFormatters';

/** Purpose: Renders shared access. Inputs: none. Output: React render output (JSX). */
export default function SharedAccess() {
  const { token } = useParams();
  const [status, setStatus] = useState('validating'); // validating, pin_required, access_granted, error
  const [error, setError] = useState('');
  const [permissionInfo, setPermissionInfo] = useState(null);
  const [pin, setPin] = useState('');
  const [pinError, setPinError] = useState('');
  const [sharedRecords, setSharedRecords] = useState([]);

  const [uploadFile, setUploadFile] = useState(null);
  const [uploadTitle, setUploadTitle] = useState('');
  const [uploadType, setUploadType] = useState('OTHER');
  const [uploadDate, setUploadDate] = useState(() => new Date().toISOString().split('T')[0]);
  const [uploadDescription, setUploadDescription] = useState('');
  const [analysisStatus, setAnalysisStatus] = useState('idle'); // idle, analyzing, success, error
  const [analysisMessage, setAnalysisMessage] = useState('');
  const [uploadStatus, setUploadStatus] = useState('idle'); // idle, uploading, success, error
  const [uploadMessage, setUploadMessage] = useState('');

  const [downloadingFileId, setDownloadingFileId] = useState(null);
  const latestAnalysisRef = useRef(0);

  const recordTypeOptions = useMemo(() => ([
    { value: 'LAB_RESULTS', label: 'Lab Results' },
    { value: 'IMAGING', label: 'Imaging' },
    { value: 'PRESCRIPTIONS', label: 'Prescriptions' },
    { value: 'CONSULTATION', label: 'Consultation Notes' },
    { value: 'IMMUNIZATIONS', label: 'Immunizations' },
    { value: 'SURGICAL', label: 'Surgical Reports' },
    { value: 'DISCHARGE', label: 'Discharge Summary' },
    { value: 'OTHER', label: 'Other' }
  ]), []);

  /** Purpose: Formats raw record type value for display. Inputs: value. Output: display label. */
  const formatRecordType = useCallback(
    (value) => formatRecordTypeLabel(value, recordTypeOptions),
    [recordTypeOptions]
  );

  /** Purpose: Executes format date. Inputs: dateStr. Output: formatted string. */
  const formatDate = useCallback((dateStr) => {
    if (!dateStr) return 'Not set';
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  }, []);

  const accessRecords = useCallback(async (pinCode = null) => {
    try {
      const response = await apiFetch(`/share/access/${token}`, {
        method: 'POST',
        body: JSON.stringify({ pin: pinCode })
      });

      const data = await response.json();

      if (!response.ok) {
        if (data.requiresPin) {
          setStatus('pin_required');
          return;
        }
        if (data.error === 'Invalid PIN') {
          setPinError('Incorrect PIN. Please try again.');
          return;
        }
        throw new Error(data.error);
      }

      setPermissionInfo(data.permission);
      setSharedRecords(Array.isArray(data.records) ? data.records : []);
      setStatus('access_granted');
    } catch (err) {
      setStatus('error');
      setError(err.message || 'Failed to access shared records.');
    }
  }, [token]);

  const validateToken = useCallback(async () => {
    try {
      const response = await apiFetch(`/share/validate/${token}`);
      const data = await response.json();

      if (!response.ok || !data.valid) {
        setStatus('error');
        setError(data.error || 'This share link is invalid or has expired.');
        return;
      }

      setPermissionInfo(data);

      if (data.requiresPin) {
        setStatus('pin_required');
      } else {
        await accessRecords();
      }
    } catch (_err) {
      setStatus('error');
      setError('Failed to validate share link. Please try again.');
    }
  }, [accessRecords, token]);

  useEffect(() => {
    validateToken();
  }, [validateToken]);

  /** Purpose: Handles pin submit. Inputs: event. Output: Operation result. */
  const handlePinSubmit = (event) => {
    event.preventDefault();
    setPinError('');
    accessRecords(pin);
  };

  /** Purpose: Handles file selection and prefill analysis. Inputs: selected file. Output: Promise with operation result. */
  const handleFileChange = useCallback(async (file) => {
    setUploadFile(file);
    setUploadStatus('idle');
    setUploadMessage('');
    setAnalysisMessage('');

    if (!file) {
      setAnalysisStatus('idle');
      return;
    }

    const requestId = Date.now();
    latestAnalysisRef.current = requestId;
    setAnalysisStatus('analyzing');
    setAnalysisMessage('Analyzing file to prefill record details…');

    try {
      const formData = new FormData();
      formData.append('file', file);
      if (pin) formData.append('pin', pin);

      const response = await apiFetch(`/share/analyze/${token}`, {
        method: 'POST',
        body: formData
      });

      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.error || 'Unable to analyze file');
      }

      if (latestAnalysisRef.current !== requestId) return;

      if (typeof data.title === 'string' && data.title.trim()) {
        setUploadTitle(data.title.trim());
      }
      if (typeof data.recordType === 'string' && data.recordType) {
        setUploadType(data.recordType);
      }
      if (typeof data.recordDate === 'string' && data.recordDate) {
        setUploadDate(data.recordDate);
      }
      if (typeof data.description === 'string') {
        setUploadDescription(data.description);
      }

      const sourceLabel = data.analysisSource === 'filename'
        ? 'filename only'
        : data.analysisSource === 'text'
          ? 'text parsing'
          : 'OCR';
      setAnalysisStatus('success');
      setAnalysisMessage(`Record fields prefilled using ${sourceLabel}. Please review before upload.`);
    } catch (err) {
      if (latestAnalysisRef.current !== requestId) return;

      const fallbackTitle = file.name ? file.name.replace(/\.[^.]+$/, '') : '';
      if (fallbackTitle) setUploadTitle(fallbackTitle);
      setAnalysisStatus('error');
      setAnalysisMessage(`${err.message || 'Analysis failed'}. You can still fill the fields manually.`);
    }
  }, [pin, token]);

  /** Purpose: Handles upload. Inputs: event. Output: Promise with operation result. */
  const handleUpload = async (event) => {
    event.preventDefault();
    setUploadMessage('');

    if (!uploadFile) {
      setUploadStatus('error');
      setUploadMessage('Please select a file to upload.');
      return;
    }

    setUploadStatus('uploading');
    try {
      const formData = new FormData();
      formData.append('file', uploadFile);
      if (uploadTitle) formData.append('title', uploadTitle);
      if (uploadType) formData.append('recordType', uploadType);
      if (uploadDate) formData.append('recordDate', uploadDate);
      if (uploadDescription) formData.append('description', uploadDescription);
      if (pin) formData.append('pin', pin);

      const response = await apiFetch(`/share/upload/${token}`, {
        method: 'POST',
        body: formData
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || 'Upload failed');
      }

      setUploadStatus('success');
      setUploadMessage('Upload successful. The record has been delivered securely.');
      setUploadFile(null);
      setUploadTitle('');
      setUploadType('OTHER');
      setUploadDate(new Date().toISOString().split('T')[0]);
      setUploadDescription('');
      setAnalysisStatus('idle');
      setAnalysisMessage('');
    } catch (err) {
      setUploadStatus('error');
      setUploadMessage(err.message || 'Upload failed');
    }
  };

  /** Purpose: Handles secure shared-file download. Inputs: fileId, fileName. Output: Promise with operation result. */
  const handleDownload = async (fileId, fileName) => {
    setDownloadingFileId(fileId);
    try {
      const response = await apiFetch(`/share/download/${token}/${fileId}`, {
        method: 'POST',
        body: JSON.stringify({ pin: pin || null })
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || 'Download failed');
      }

      const blob = await response.blob();
      const objectUrl = window.URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = objectUrl;
      anchor.download = fileName || 'medical-record-file';
      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);
      window.URL.revokeObjectURL(objectUrl);
    } catch (err) {
      window.alert(err.message || 'Failed to download file');
    } finally {
      setDownloadingFileId(null);
    }
  };

  if (status === 'validating') return <SharedAccessValidatingState />;
  if (status === 'error') return <SharedAccessErrorState error={error} />;
  if (status === 'pin_required') {
    return (
      <SharedAccessPinRequiredState
        permissionInfo={permissionInfo}
        pin={pin}
        pinError={pinError}
        onPinChange={setPin}
        onSubmit={handlePinSubmit}
        formatDate={formatDate}
      />
    );
  }

  const canUploadToShare = Boolean(permissionInfo?.providerRequestId);
  const showSharedRecords = !canUploadToShare;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
      <header className="bg-white border-b border-slate-200 shadow-sm">
        <div className="max-w-4xl mx-auto px-6 py-4">
          <div className="flex items-center gap-3">
            <div className="bg-gradient-to-br from-blue-600 to-indigo-600 p-2 rounded-xl">
              <Shield className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-slate-900">MedCaddy</h1>
              <p className="text-xs text-slate-500">Secure Medical Records</p>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-8 space-y-8">
        {canUploadToShare && (
          <SharedUploadView
            ownerName={permissionInfo?.ownerName}
            recordTypeOptions={recordTypeOptions}
            uploadTitle={uploadTitle}
            setUploadTitle={setUploadTitle}
            uploadType={uploadType}
            setUploadType={setUploadType}
            uploadDate={uploadDate}
            setUploadDate={setUploadDate}
            uploadDescription={uploadDescription}
            setUploadDescription={setUploadDescription}
            analysisStatus={analysisStatus}
            analysisMessage={analysisMessage}
            uploadStatus={uploadStatus}
            uploadMessage={uploadMessage}
            onFileChange={handleFileChange}
            onSubmit={handleUpload}
          />
        )}

        <SharedRequestDetails
          permissionInfo={permissionInfo}
          formatDate={formatDate}
          formatRecordType={formatRecordType}
        />

        {showSharedRecords && (
          <SharedDownloadView
            sharedRecords={sharedRecords}
            downloadingFileId={downloadingFileId}
            onDownload={handleDownload}
            formatRecordType={formatRecordType}
            formatDate={formatDate}
          />
        )}

        <div className="mt-8 text-center text-sm text-slate-500">
          <p>This is a secure, time-limited share from MedCaddy.</p>
          <p>Access expires on {formatDate(permissionInfo?.expiresAt)}.</p>
        </div>
      </main>
    </div>
  );
}
